import React, { useState } from "react";
import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import Flx from "../../../components/layout/Flx";
import colors from "../../../theme/colors";
import { formatFileSize, submissionDocuments } from "../submissionHelpers";
import { useSubmissionFileDownload } from "../useSubmissionFileDownload";

/**
 * What the borrower attached — photo ID, bank statements, whatever the form
 * took — with a download per file.
 *
 * Listed from `uploadMetadata`, which is the only document detail the
 * submission endpoints return; the download resolves the storage name
 * separately (see useSubmissionFileDownload).
 */
const DocumentRow = ({ file, submission, isLast }) => {
  const download = useSubmissionFileDownload();
  const [busy, setBusy] = useState(false);
  const name = file.file_display_name || file.name || "Document";

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await download(file, submission);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Flx
      centerY
      gap={1}
      sx={{
        py: "8px",
        borderBottom: isLast ? "none" : `1px solid ${colors.slate100}`,
        fontSize: 13,
        minWidth: 0,
      }}
    >
      <InsertDriveFileOutlinedIcon
        sx={{ fontSize: 16, color: colors.slate400, flexShrink: 0 }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            fontWeight: 550,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </Box>
        <Box sx={{ fontSize: 11.5, color: colors.slate500 }}>
          {[file.docType, formatFileSize(file.size)].filter(Boolean).join(" · ")}
        </Box>
      </Box>
      <Tooltip title={`Download ${name}`} arrow>
        {/* A span so the tooltip keeps a live child while the button is
            disabled — MUI can't bind to a disabled element. */}
        <span>
          <IconButton
            size="small"
            aria-label={`Download ${name}`}
            disabled={busy}
            onClick={handleClick}
            sx={{ color: colors.slate500 }}
          >
            {busy ? (
              <CircularProgress size={14} thickness={5} />
            ) : (
              <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </Flx>
  );
};

const SubmissionDocumentsSection = ({ submission }) => {
  const files = submissionDocuments(submission);

  if (!files.length) {
    return (
      <Box sx={{ py: "8px", fontSize: 13, color: colors.slate400 }}>
        No documents attached.
      </Box>
    );
  }

  return files.map((file, i) => (
    <DocumentRow
      key={file.id || file.oid || `${file.name}-${i}`}
      file={file}
      submission={submission}
      isLast={i === files.length - 1}
    />
  ));
};

export default SubmissionDocumentsSection;
